import { useEffect, useMemo, useRef, useState } from 'react';
import {
  oversightAlertFamily,
  oversightEventPath,
  oversightEvents,
  oversightFreezeContact,
  oversightHeldDecide,
  oversightModeled,
  oversightPatterns,
  oversightTellNurse,
  type FacilitySearchRow,
  type OversightEvent,
  type OversightHeldDecision,
} from '@aithora/shared';
import { Link } from 'react-router-dom';
import { CARE_STAFF_ROLES } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { Act, ReadOnlyAs, answersHeld, guardLine, isFacilityAdmin, localClock, useApi, useEventFeed, useNow, verdictTag } from './common';
import { AboutThis } from '../../ui/AboutThis';
import { FacilitySearch } from './FacilitySearch';
import type { LedgerIntent, OversightTab } from './OversightPage';
import { BuildLabel, BuildTagged } from '../../ui/buildLabels';

/** The client keeps the latest 500 events; everything older is read from the Truth Ledger. */
const CAP = 500;
const SHOWN_ROWS = 60;

type Tag = 'ALLOW' | 'LOG' | 'HOLD' | 'BLOCK';
// design ldVals: VC
const VC: Record<Tag, [string, string]> = { ALLOW: ['#1F6B52', 'rgba(31,107,82,.10)'], LOG: ['#54606C', 'rgba(34,39,46,.07)'], HOLD: ['#2F5C97', 'rgba(47,92,151,.12)'], BLOCK: ['#B3261E', 'rgba(179,38,30,.10)'] };
const vStyle = (v: Tag) => 'flex:0 0 54px;display:inline-flex;align-items:center;justify-content:center;height:22px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.07em;color:' + VC[v][0] + ';background:' + VC[v][1] + ';';
const TYPE_LABEL: Record<string, string> = { decision: 'Decision', companion: 'Atlas', sensor: 'Sensor', reminder: 'Reminder', escalation: 'Escalation', care_log: 'Care log', ledger: 'Ledger' };
const TYPES = ['All types', 'Decision', 'Atlas', 'Sensor', 'Reminder', 'Escalation', 'Care log'];
const GROUPABLE = new Set(['companion', 'sensor', 'reminder', 'care_log']);
const btnP = 'display:inline-flex;align-items:center;justify-content:center;min-height:36px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';

/** The Live Decisions verdict: allowed, held, blocked, or simply logged. */
export function ldTag(e: OversightEvent): Tag {
  const v = verdictTag(e);
  if (v === 'ALLOW' || v === 'HOLD' || v === 'BLOCK') return v;
  if (v === 'ALERT') return 'HOLD';
  return 'LOG';
}
/** The raw event on one line, keys bare: {type:"decision", verdict:"HOLD", ms:46}. */
export const rawLine = (raw: Record<string, unknown>) => '{' + Object.entries(raw).map(([k, v]) => k + ':' + JSON.stringify(v)).join(', ') + '}';
const what = (e: OversightEvent) => e.summary ?? TYPE_LABEL[e.type] ?? e.type;
const ref = (e: OversightEvent) => e.ref ?? e.id;

type Row =
  | { kind: 'group'; key: string; e: OversightEvent; n: number; facN: number; open: boolean; members: OversightEvent[] }
  | { kind: 'one'; key: string; e: OversightEvent; child: boolean };

type Pending =
  | { kind: 'held'; e: OversightEvent; decision: OversightHeldDecision; label: string }
  | { kind: 'freeze'; residentId: string; who: string }
  | { kind: 'family'; residentId: string; who: string }
  | { kind: 'nurse'; guard: string };

export function LiveDecisions({ onGo }: { onGo: (t: OversightTab, i?: LedgerIntent) => void }) {
  const { role } = useConsoleRole();
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const fa = isFacilityAdmin(role);
  const cs = !!role && CARE_STAFF_ROLES.has(role);
  const op = !fa && !cs;
  // V9: only the building's own admin acts here; every Aithora seat and the Owner/Operator read.
  const canAct = answersHeld(role);
  const now = useNow(1000);

  const [paused, setPaused] = useState(false);
  const [hoverPause, setHoverPause] = useState(false);
  const [replay, setReplay] = useState(0);
  const [vf, setVf] = useState<'All' | 'Allow' | 'Hold' | 'Block'>('All');
  const [tf, setTf] = useState('All types');
  const [fac, setFac] = useState<FacilitySearchRow | null>(null);
  const [q, setQ] = useState('');
  const [openG, setOpenG] = useState<Record<string, boolean>>({});
  const [sel, setSel] = useState<OversightEvent | null>(null);
  const [acts, setActs] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Pending | null>(null);
  const frozen = useRef<OversightEvent[] | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const replaying = replay > 0;
  const live = useEventFeed({ windowMs: 60 * 60_000, limit: 1000 });
  const past = useApi(
    (c, signal) => {
      const to = Date.now() - replay * 60_000;
      return oversightEvents(c, { query: { from: new Date(to - 60 * 60_000).toISOString(), to: new Date(to).toISOString(), limit: 1000 }, signal });
    },
    [replay],
    { enabled: replaying },
  );
  const patterns = useApi((c, signal) => oversightPatterns(c, { signal }), [], { pollMs: 30000 });
  const modeled = useApi((c, signal) => oversightModeled(c, { signal }), [], { pollMs: 60000 });
  const path = useApi((c, signal) => oversightEventPath(c, { params: { id: sel?.id ?? '' }, signal }), [sel?.id], { enabled: !!sel });

  // newest first, capped
  const liveWin = useMemo(() => [...(live.events ?? [])].reverse().slice(0, CAP), [live.events]);
  useEffect(() => {
    if (!paused) frozen.current = null;
    else if (!frozen.current) frozen.current = liveWin;
  }, [paused, liveWin]);
  const shownLive = paused && frozen.current ? frozen.current : liveWin;
  const newN = paused && frozen.current ? liveWin.filter((e) => !frozen.current!.some((f) => f.id === e.id)).length : 0;
  const all = replaying ? [...(past.data?.events ?? [])].reverse().slice(0, CAP) : shownLive;
  const win = fac ? all.filter((e) => e.facility?.id === fac.id) : all;
  const cnt = (t: Tag) => win.filter((e) => ldTag(e) === t).length;

  const term = q.trim().toLowerCase();
  const shownAll = win.filter(
    (e) =>
      (vf === 'All' || ldTag(e) === vf.toUpperCase()) &&
      (tf === 'All types' || TYPE_LABEL[e.type] === tf) &&
      (!term || [what(e), e.resident?.display, e.resident?.initials, e.guard, ref(e)].filter(Boolean).join(' ').toLowerCase().includes(term)),
  );

  // Routine runs of three or more collapse into one row (design ldVals: grouping).
  const rows: Row[] = [];
  const list = shownAll.slice(0, 160);
  for (let i = 0, n = 0; i < list.length && n < SHOWN_ROWS; ) {
    const e = list[i];
    const t = ldTag(e);
    if (GROUPABLE.has(e.type) && (t === 'ALLOW' || t === 'LOG')) {
      let j = i;
      while (j + 1 < list.length && GROUPABLE.has(list[j + 1].type) && what(list[j + 1]) === what(e) && ldTag(list[j + 1]) === t) j++;
      const count = j - i + 1;
      if (count >= 3) {
        const members = list.slice(i, j + 1);
        const key = 'g' + e.id;
        rows.push({ kind: 'group', key, e, n: count, facN: new Set(members.map((x) => x.facility?.id ?? '')).size, open: !!openG[key], members });
        n++;
        if (openG[key]) for (const m of members) rows.push({ kind: 'one', key: m.id, e: m, child: true }), n++;
        i = j + 1;
        continue;
      }
    }
    rows.push({ kind: 'one', key: e.id, e, child: false });
    n++;
    i++;
  }

  // Rate now against the usual rate for this hour (MODELED).
  const perMin = liveWin.filter((e) => now - Date.parse(e.at) < 60_000).length;
  const usual = modeled.data ? modeled.data.usualRateThisHour.perVerdict.reduce((a, v) => a + v.avgPerHour, 0) / 60 : null;

  // Alerts: the first scam pattern, and the guard closest to tripping.
  const pat = patterns.data?.patterns[0] ?? null;
  const trip = (modeled.data?.beforeItTrips ?? []).filter((t) => t.minutesToTrip !== null).sort((a, b) => (a.minutesToTrip ?? 0) - (b.minutesToTrip ?? 0))[0] ?? null;
  const alerts: { key: string; tag: string; tagC: string; modeled: boolean; head: string; sub: string; bg: string; done: string; acts: { label: string; style: string; go: () => void }[] }[] = [];
  if (pat) {
    const who = pat.resident.display ?? pat.resident.initials ?? 'one resident';
    alerts.push({
      key: 'pat',
      tag: 'PATTERN',
      tagC: '#B3261E',
      modeled: false,
      head: pat.count + ' scam blocks for ' + who + ' in 20 minutes' + (pat.callerCaptured ? ', same caller' : ''),
      sub: 'Each one blocked on its own. Together they look like a campaign, not a mistake.' + (op && pat.facilityName ? ' ' + pat.facilityName + '.' : ''),
      bg: 'rgba(179,38,30,.07)',
      done: acts.pat ?? '',
      acts:
        acts.pat || !canAct
          ? []
          : [
              { label: 'Freeze contact', style: btnP + 'background:#B3261E;color:#fff;', go: () => setPending({ kind: 'freeze', residentId: pat.residentId, who }) },
              { label: 'Alert family', style: btnP + 'border:1px solid rgba(179,38,30,.45);color:#B3261E;', go: () => setPending({ kind: 'family', residentId: pat.residentId, who }) },
            ],
    });
  }
  if (trip) {
    alerts.push({
      key: 'trip',
      tag: 'BEFORE IT TRIPS',
      tagC: '#264C7E',
      modeled: true,
      head: (trip.name ?? trip.guard) + ' at ' + (op ? (trip.facilityName ?? 'a building') : 'your building') + ' trips in ~' + Math.round(trip.minutesToTrip ?? 0) + ' min at this rate',
      sub: 'Headroom used ' + Math.round((trip.lastHour / Math.max(1, trip.threshold)) * 100) + '% and climbing. ' + (trip.effect ?? 'At the trip line, what it guards is held for a person.'),
      bg: 'rgba(47,92,151,.07)',
      done: acts.trip ?? '',
      acts: acts.trip || !canAct ? [] : [{ label: 'Tell the on-shift nurse', style: btnP + 'border:1px solid rgba(47,92,151,.45);color:#264C7E;', go: () => setPending({ kind: 'nurse', guard: trip.guard }) }],
    });
  }

  const replayAt = replaying ? new Date(now - replay * 60_000) : null;
  const liveLabel = replaying ? 'REPLAY' : paused ? 'PAUSED' : 'LIVE';
  const fixedFac = fa || cs ? (all[0]?.facility?.name ?? '') + (cs ? ' · your floor' : '') : '';

  const pause = () => {
    setPaused((p) => !p);
    setHoverPause(false);
  };

  const dlg = (() => {
    if (!pending) return null;
    if (pending.kind === 'held') {
      return {
        title: pending.label + ': ' + what(pending.e),
        body: 'Your decision is sealed to the Truth Ledger with your name and the time.',
        label: pending.label,
        run: async () => {
          await oversightHeldDecide(client, { params: { id: pending.e.id }, body: withConfirm({ decision: pending.decision }) });
          setActs((a) => ({ ...a, [pending.e.id]: pending.decision === 'allow' ? 'Allowed by you' : pending.decision === 'refuse' ? 'Refused by you' : 'Routed to nurse' }));
        },
      };
    }
    if (pending.kind === 'freeze') {
      return {
        title: 'Freeze this contact for ' + pending.who + '?',
        body: 'Recorded and sealed to the Truth Ledger. Calls and messages are not intercepted yet, so nothing blocks the contact until that is live.',
        label: 'Freeze contact',
        run: async () => {
          await oversightFreezeContact(client, { body: withConfirm({ residentId: pending.residentId, reason: 'Repeated scam blocks' }) });
          setActs((a) => ({ ...a, pat: 'Contact frozen' }));
        },
      };
    }
    if (pending.kind === 'family') {
      return {
        title: 'Alert ' + pending.who + "'s family?",
        body: 'Each linked family member who may receive alerts gets one. Sealed to the Truth Ledger.',
        label: 'Alert family',
        run: async () => {
          await oversightAlertFamily(client, { body: withConfirm({ residentId: pending.residentId, reason: 'Repeated scam blocks' }) });
          setActs((a) => ({ ...a, pat: 'Family alerted' }));
        },
      };
    }
    const guard = pending.guard;
    return {
      title: 'Tell the on-shift nurse?',
      body: 'The nurse on shift is told this guard is close to its trip line.',
      label: 'Tell the nurse',
      run: async () => {
        await oversightTellNurse(client, { body: withConfirm({ guard }) });
        setActs((a) => ({ ...a, trip: 'Nurse told' }));
      },
    };
  })();

  const rowStyle = (edge: string, isSel: boolean) =>
    'animation:riseIn .32s cubic-bezier(.22,.61,.36,1);flex:0 0 auto;display:flex;align-items:center;gap:6px 12px;min-height:44px;padding:5px 8px 5px 10px;text-decoration:none;border-bottom:1px solid rgba(34,39,46,.07);border-left:3px solid ' + edge + ';' + (isSel ? 'background:rgba(47,92,151,.08);' : '');
  const metaOf = (e: OversightEvent) =>
    [e.resident ? [e.resident.display ?? e.resident.initials, e.resident.room ? 'Rm ' + e.resident.room : null].filter(Boolean).join(' · ') : null, op ? e.facility?.name : null, guardLine(e) || null, cs ? null : ref(e)]
      .filter(Boolean)
      .join(' · ');

  // The selected event's detail (the gate path comes from /oversight/events/:id/path).
  const st = sel ? ldTag(sel) : null;
  const selDone = sel ? acts[sel.id] : undefined;
  const p = path.data;
  const fired = st === 'BLOCK' || st === 'HOLD';

  return (
    <div data-screen-label="Live Decisions" style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:8px;min-width:0;overflow-y:auto;overflow-x:hidden;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 18px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('display:inline-flex;align-items:center;gap:7px;font-size:14px;font-weight:800;letter-spacing:.1em;color:' + (replaying ? '#264C7E' : paused ? '#3C454F' : '#1F6B52') + ';')}>
          <span style={sx('width:8px;height:8px;border-radius:50%;background:' + (replaying ? '#2F5C97' : paused ? '#54606C' : '#1F6B52') + ';box-shadow:0 0 0 1px rgba(31,107,82,.18);transition:box-shadow .9s ease;')} />
          {liveLabel}
        </span>
        {(
          [
            ['allowed', cnt('ALLOW'), '#1F6B52'],
            ['logged', cnt('LOG'), '#22272E'],
            ['held', cnt('HOLD'), '#2F5C97'],
            ['blocked', cnt('BLOCK'), '#B3261E'],
          ] as const
        ).map(([k, v, c]) => (
          <span key={k} style={sx('display:inline-flex;align-items:baseline;gap:6px;white-space:nowrap;')}>
            <span style={sx('font-size:20px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;transition:color .3s;color:' + c + ';')}>{String(v)}</span>
            <span style={sx('font-size:16px;color:#3C454F;')}>{k}</span>
          </span>
        ))}
        <span title="The usual rate is modeled from the same hour over the last four weeks" style={sx('display:inline-flex;align-items:center;gap:6px;font-size:16px;color:#3C454F;white-space:nowrap;')}>
          {perMin.toFixed(1) + '/min now · ' + (usual === null ? '–' : usual.toFixed(1)) + ' usual for this hour'}{' '}
          <BuildLabel><span style={sx('display:inline-flex;align-items:center;font-size:14px;font-weight:700;letter-spacing:.09em;color:#264C7E;background:rgba(47,92,151,.12);border-radius:4px;padding:2px 6px;white-space:nowrap;')}>MODELED</span></BuildLabel>
        </span>
        <span style={sx('flex:1 1 10px;')} />
        <span style={sx('display:inline-flex;align-items:center;gap:8px;min-width:0;')}>
          <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;')}>Replay</span>
          <input
            type="range"
            min="0"
            max="1440"
            step="5"
            value={replay}
            onChange={(e) => {
              const v = +e.target.value;
              setReplay(v);
              if (v > 0) setPaused(false);
            }}
            aria-label="Replay the last 24 hours"
            style={sx('width:150px;accent-color:#2F5C97;')}
          />
          <span style={sx('font-size:16px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;white-space:nowrap;')}>
            {replayAt ? replayAt.toTimeString().slice(0, 5) + ' · ' + Math.floor(replay / 60) + 'h ' + (replay % 60) + 'm ago' : 'Live'}
          </span>
        </span>
        <Act onClick={pause} css={btnP + 'min-height:34px;border:1px solid rgba(34,39,46,.2);color:#22272E;'}>
          {paused ? 'Resume' : 'Pause'}
        </Act>
      </div>

      {alerts.length > 0 && (
        <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr));gap:8px 22px;min-width:0;')}>
          {alerts.map((al) => (
            <div key={al.key} role="note" aria-label={al.tag} style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;padding:9px 12px;border-radius:10px;background:' + al.bg + ';min-width:0;')}>
              <span style={sx('display:flex;flex-direction:column;gap:2px;flex:1 1 220px;min-width:0;')}>
                <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                  <BuildTagged tag={al.tag}><span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;color:' + al.tagC + ';')}>{al.tag}</span></BuildTagged>
                  {al.modeled && <BuildLabel><span style={sx('display:inline-flex;align-items:center;font-size:14px;font-weight:700;letter-spacing:.09em;color:#264C7E;background:rgba(47,92,151,.12);border-radius:4px;padding:2px 6px;white-space:nowrap;')}>MODELED</span></BuildLabel>}
                </span>
                <span style={sx('font-size:16px;font-weight:800;color:#22272E;line-height:1.3;')}>{al.head}</span>
                <span style={sx('font-size:16px;color:#3C454F;line-height:1.35;')}>{al.sub}</span>
              </span>
              <span style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px;white-space:nowrap;')}>
                {al.acts.map((x) => (
                  <Act key={x.label} css={x.style} onClick={x.go} disabled={!!roReason} title={roReason ?? undefined}>
                    {x.label}
                  </Act>
                ))}
                {al.done && <span style={sx('font-size:16px;font-weight:800;color:#1F6B52;white-space:nowrap;')}>✓ {al.done}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);padding-bottom:7px;')}>
        <span role="group" aria-label="Verdict" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.16);border-radius:9px;overflow:hidden;')}>
          {(['All', 'Allow', 'Hold', 'Block'] as const).map((v) => (
            <Act key={v} pressed={vf === v} onClick={() => setVf(v)} css={'display:inline-flex;align-items:center;min-height:34px;padding:0 12px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;font-variant-numeric:tabular-nums;' + (vf === v ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;')}>
              {v === 'All' ? 'All ' + win.length : v + ' ' + cnt(v.toUpperCase() as Tag)}
            </Act>
          ))}
        </span>
        <select value={tf} onChange={(e) => setTf(e.target.value)} aria-label="Event type" style={sx('min-height:34px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FCFAF4;font-family:inherit;font-size:16px;font-weight:600;color:#22272E;padding:0 10px;')}>
          {TYPES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {op && <FacilitySearch value={fac} onPick={setFac} />}
        {fixedFac && <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{fixedFac}</span>}
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resident, guard, entry id" aria-label="Search" style={sx('flex:1 1 180px;min-width:0;min-height:34px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FCFAF4;font-family:inherit;font-size:16px;color:#22272E;padding:0 10px;')} />
        <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{shownAll.length + ' of ' + win.length + ' events'}</span>
      </div>

      <div style={sx('flex:1 0 420px;min-height:420px;display:flex;flex-wrap:wrap;align-items:stretch;gap:14px 24px;min-width:0;')}>
        <div style={sx('flex:1.65 1 440px;min-width:0;min-height:420px;display:flex;flex-direction:column;position:relative;')}>
          {newN > 0 && !replaying && (
            <Act
              onClick={() => {
                if (logRef.current) logRef.current.scrollTop = 0;
                setPaused(false);
              }}
              css="position:absolute;top:6px;left:50%;transform:translateX(-50%);z-index:2;display:inline-flex;align-items:center;gap:6px;min-height:34px;padding:0 14px;border-radius:17px;background:#22272E;color:#FCFAF4;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;box-shadow:0 4px 14px rgba(34,39,46,.18);"
            >
              {newN + ' new · Jump to live ↑'}
            </Act>
          )}
          {replayAt && (
            <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;padding:6px 10px;border-radius:8px;background:rgba(47,92,151,.10);margin:6px 0 2px;')}>
              <span style={sx('font-size:16px;font-weight:800;color:#264C7E;')}>{'Replaying ' + replayAt.toTimeString().slice(0, 5) + ' exactly as it happened · every row is a ledger entry'}</span>
              <span style={sx('flex:1 1 10px;')} />
              <Act onClick={() => setReplay(0)} css="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;">
                Back to live
              </Act>
            </div>
          )}
          <div
            ref={logRef}
            role="log"
            aria-live="polite"
            aria-label="Live decisions"
            onScroll={(e) => {
              if (e.currentTarget.scrollTop > 12 && !paused) setPaused(true);
            }}
            onMouseEnter={() => {
              if (!paused && !replaying) {
                setPaused(true);
                setHoverPause(true);
              }
            }}
            onMouseLeave={(e) => {
              if (hoverPause && e.currentTarget.scrollTop <= 12) {
                setPaused(false);
                setHoverPause(false);
              }
            }}
            style={sx('flex:1 1 0;min-height:360px;overflow-y:auto;overflow-x:hidden;display:flex;flex-direction:column;padding-right:2px;')}
          >
            {(live.events || past.data) && rows.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:16px 2px;')}>Nothing matches this filter in the latest 500 events.</div>}
            {(replaying ? past.error : live.error) && <div style={sx('font-size:16px;color:#54606C;padding:16px 2px;')}>{(replaying ? past.error : live.error)?.message}</div>}
            {rows.map((r) => {
              const e = r.e;
              const t = ldTag(e);
              if (r.kind === 'group') {
                const facName = r.facN === 1 ? (fa || cs ? e.facility?.name : e.facility?.name) : r.facN + ' facilities';
                return (
                  <Act key={r.key} css={rowStyle('transparent', false)} onClick={() => setOpenG((o) => ({ ...o, [r.key]: !o[r.key] }))} pressed={r.open}>
                    <span style={sx('flex:0 0 58px;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{localClock(new Date(e.at))}</span>
                    <span style={sx(vStyle(t))}>{t}</span>
                    <span style={sx('flex:1 1 200px;min-width:0;display:flex;flex-direction:column;gap:1px;')}>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.3;')}>{what(e) + ' ×' + r.n + (t === 'ALLOW' ? ' · all allowed' : ' · all logged')}</span>
                      <span style={sx('font-size:14px;color:#54606C;line-height:1.3;')}>{[TYPE_LABEL[e.type] ?? e.type, facName, r.open ? 'click to collapse' : 'click to expand'].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span style={sx('flex:0 0 46px;text-align:right;font-size:14px;font-weight:700;color:#3C454F;font-variant-numeric:tabular-nums;')} />
                  </Act>
                );
              }
              return (
                <Act key={r.key} css={rowStyle(t === 'BLOCK' ? '#B3261E' : t === 'HOLD' ? '#2F5C97' : 'transparent', sel?.id === e.id)} onClick={() => setSel(e)} pressed={sel?.id === e.id}>
                  <span style={sx('flex:0 0 58px;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{localClock(new Date(e.at))}</span>
                  <span style={sx(vStyle(t))}>{t}</span>
                  <span style={sx('flex:1 1 200px;min-width:0;display:flex;flex-direction:column;gap:1px;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.3;')}>{(r.child ? '└ ' : '') + what(e)}</span>
                    <span style={sx('font-size:14px;color:#54606C;line-height:1.3;')}>{metaOf(e)}</span>
                  </span>
                  <span style={sx('flex:0 0 46px;text-align:right;font-size:14px;font-weight:700;color:#3C454F;font-variant-numeric:tabular-nums;')}>{e.latencyMs !== null ? Math.round(e.latencyMs) + 'ms' : ''}</span>
                </Act>
              );
            })}
            <div style={sx('font-size:14px;color:#54606C;padding:10px 2px;')}>{'Showing the latest ' + CAP + ' events. Everything older is in the Truth Ledger.'}</div>
          </div>
        </div>

        <div style={sx('flex:1 1 290px;min-width:0;min-height:420px;position:relative;border-left:1px solid rgba(34,39,46,.10);')}>
          <div style={sx('position:absolute;inset:0;overflow-y:auto;display:flex;flex-direction:column;gap:12px;padding-left:18px;')}>
            {/* V9: the explainer opens on the "i" (design k16049). */}
            {!sel && (
              <AboutThis inline textStyle="font-size:16px;color:#54606C;padding:6px 0;line-height:1.45;">
                Pick any row to see how the gate decided it: every step with its timing, the rule that fired, the raw event and its ledger entry.
              </AboutThis>
            )}
            {sel && st && (
              <>
                <div style={sx('display:flex;flex-direction:column;gap:3px;')}>
                  <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                    <span style={sx(vStyle(st))}>{st}</span>
                    <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{localClock(new Date(sel.at)) + (sel.latencyMs !== null ? ' · ' + Math.round(sel.latencyMs) + 'ms total' : '')}</span>
                  </span>
                  <span style={sx('font-size:16px;font-weight:800;color:#22272E;line-height:1.3;')}>{what(sel)}</span>
                  <span style={sx('font-size:16px;color:#3C454F;')}>
                    {[sel.resident ? [sel.resident.display ?? sel.resident.initials, sel.resident.room ? 'Rm ' + sel.resident.room : null].filter(Boolean).join(' · ') : null, sel.facility?.name, TYPE_LABEL[sel.type]].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {st === 'HOLD' && canAct && !selDone && sel.id.startsWith('cmp:') && (
                  <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                    {(
                      [
                        ['Allow', 'allow', '#1F6B52'],
                        ['Refuse', 'refuse', '#B3261E'],
                        ['Route to nurse', 'route', '#2F5C97'],
                      ] as const
                    ).map(([label, d, c], xi) => (
                      <Act key={d} disabled={!!roReason} title={roReason ?? undefined} onClick={() => setPending({ kind: 'held', e: sel, decision: d, label })} css={btnP + (xi === 2 ? 'background:#2F5C97;color:#fff;' : 'border:1px solid rgba(34,39,46,.2);color:' + c + ';')}>
                        {label}
                      </Act>
                    ))}
                  </div>
                )}
                {st === 'HOLD' && !canAct && !selDone && sel.id.startsWith('cmp:') && <ReadOnlyAs role={role} />}
                {selDone && <span style={sx('font-size:16px;font-weight:800;color:#1F6B52;')}>✓ {selDone}</span>}
                {(sel.type === 'escalation' || /chest pain|distress|bank card|gift card/i.test(sel.summary ?? '')) && (
                  <Link to="/guardian" style={sx('display:flex;align-items:center;gap:8px;min-height:36px;padding:0 10px;border-radius:8px;background:rgba(179,38,30,.07);text-decoration:none;min-width:0;')}>
                    <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#B3261E;')}>GUARDIAN</span>
                    <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#22272E;')}>Also open in Guardian, with an owner and a clock</span>
                    <span style={sx('font-size:16px;font-weight:700;color:#2F5C97;')}>→</span>
                  </Link>
                )}
                <div style={sx('display:flex;flex-direction:column;')}>
                  <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;padding-bottom:4px;border-bottom:1px solid rgba(34,39,46,.12);')}>Path through the gate</span>
                  {path.loading && <span style={sx('font-size:14px;color:#54606C;padding:5px 0;')}>Loading the path…</span>}
                  {p && p.stages.length === 0 && <span style={sx('font-size:14px;color:#54606C;padding:5px 0;')}>{p.note ?? 'No gate stages for this event.'}</span>}
                  {(p?.stages ?? []).map((s, i) => {
                    const hit = s.result !== 'PASS' && s.result !== 'CLEAR';
                    return (
                      <span key={i} style={sx('display:grid;grid-template-columns:14px minmax(0,1fr) auto;gap:8px;align-items:center;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                        <span style={sx('display:block;width:10px;height:10px;border-radius:50%;background:' + (hit && fired ? VC[st][0] : '#1F6B52') + ';')} />
                        <span style={sx('font-size:16px;color:#22272E;font-weight:600;')}>
                          {[s.layer, s.name].filter(Boolean).join(' ')}
                          <span style={sx('display:block;font-size:14px;color:#54606C;font-weight:500;')}>{s.reason ?? (hit ? s.result.toLowerCase() : 'passed')}</span>
                        </span>
                        <span style={sx('font-size:14px;font-weight:700;color:#3C454F;font-variant-numeric:tabular-nums;')}>{s.latencyMs !== null ? Math.round(s.latencyMs) + 'ms' : '–'}</span>
                      </span>
                    );
                  })}
                </div>
                <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
                  <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;')}>Evidence</span>
                  {[
                    ...(cs ? [] : [['Entry', ref(sel)], ['Thread', sel.threadId ?? '–']]),
                    ['Rule', p?.ruleFired ? p.ruleFired.name + (sel.level ? ' · level ' + sel.level : '') : guardLine(sel) || '–'],
                    ['Raw event', p?.raw ? rawLine(p.raw) : '–'],
                    ...(cs ? [] : [['Ledger hash', p?.ledgerHash ? 'sha256:' + p.ledgerHash : 'not sealed at write time']]),
                  ].map(([k, v]) => (
                    <span key={k} style={sx('display:grid;grid-template-columns:92px minmax(0,1fr);gap:8px;font-size:14px;')}>
                      <span style={sx('color:#54606C;')}>{k}</span>
                      <span style={sx('color:#22272E;font-weight:600;font-variant-numeric:tabular-nums;word-break:break-all;')}>{v}</span>
                    </span>
                  ))}
                </div>
                <div style={sx('display:flex;align-items:center;gap:6px 16px;flex-wrap:wrap;')}>
                  {sel.threadId && (
                    <Link to={'/atlas?tab=conversations&thread=' + encodeURIComponent(sel.threadId)} style={sx('display:inline-flex;align-items:center;min-height:34px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;')}>
                      Open thread
                    </Link>
                  )}
                  <Act onClick={() => onGo('ledger', { query: sel.ref ?? sel.ledgerEntryId ?? '' })} css="display:inline-flex;align-items:center;min-height:34px;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;">
                    Open in Truth Ledger
                  </Act>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {dlg && <ConfirmDialog title={dlg.title} body={dlg.body} confirmLabel={dlg.label} onConfirm={dlg.run} onClose={() => setPending(null)} />}
    </div>
  );
}
